/**
 * EvolutionTab — version timeline with Gource-style animated graph transitions.
 *
 * The timeline features a smooth scrubber that:
 *  - Animates in real-time as the graph transitions (driven by EvolutionGraph.onProgress)
 *  - Can be clicked to jump to any version
 *  - Can be dragged (pointer events) to scrub through versions
 *
 * Graph uses EvolutionGraph which maintains a persistent Sigma.js graph and
 * animates version changes with staggered node additions, fade-outs, and
 * a continuous FA2 web-worker for organic settling.
 */
import { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus, Zap, GitBranch } from 'lucide-react';
import { clsx } from 'clsx';
import { useOntologyStore } from '@/stores/ontology';
import type { OntologyVersionInfo, OntologyDiff } from '@/services/api';
import { EvolutionGraph, type EvolutionGraphHandle } from './EvolutionGraph';
import { NodeInspectorPanel } from './NodeInspectorPanel';

const PLAY_DWELL_MS = 3500;

// ─── Scrubber timeline ────────────────────────────────────────────────────────
/**
 * scrubberValue: fractional position across all versions.
 * 0 = fully at version[0], 1 = fully at version[1], … N-1 = fully at version[N-1]
 * 1.4 = 40% through the transition from version[1] → version[2]
 */
function VersionScrubber({
  versions,
  scrubberValue,
  onSeek,
  disabled,
}: {
  versions: OntologyVersionInfo[];
  scrubberValue: number;          // 0 … versions.length-1
  onSeek: (idx: number) => void;  // called with integer version index on click/drag-release
  disabled: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const n = versions.length;
  if (n < 2) return null;

  // The scrubber % position across the full track
  const scrubPct = (scrubberValue / (n - 1)) * 100;

  // Convert a clientX position into a fractional version index
  const clientXToIdx = useCallback((clientX: number): number => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * (n - 1));
  }, [n]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = true;
  }, [disabled]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    // Visual-only: show hover
    setHoverIdx(clientXToIdx(e.clientX));
  }, [clientXToIdx]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const idx = clientXToIdx(e.clientX);
    setHoverIdx(null);
    onSeek(idx);
  }, [clientXToIdx, onSeek]);

  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (disabled) return;
    const idx = clientXToIdx(e.clientX);
    onSeek(idx);
  }, [disabled, clientXToIdx, onSeek]);

  return (
    <div className="relative select-none px-2 pt-2 pb-6">
      {/* ── Track: background + filled bar ── */}
      <div
        ref={trackRef}
        className="relative h-10 flex items-center cursor-pointer"
        onClick={handleTrackClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => { if (!draggingRef.current) setHoverIdx(null); }}
      >
        {/* Background track */}
        <div className="absolute inset-x-0 h-1 bg-navy-700 rounded-full" style={{ top: '50%', transform: 'translateY(-50%)' }} />

        {/* Filled teal bar from start to scrubber position */}
        <div
          className="absolute h-1 bg-brand-teal rounded-full origin-left"
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            width: `${scrubPct}%`,
            transition: 'width 16ms linear',
          }}
        />

        {/* Segment dividers + version circles */}
        {versions.map((v, i) => {
          const pct = (i / (n - 1)) * 100;
          const isActive = Math.round(scrubberValue) === i;
          const isPast   = scrubberValue > i - 0.05;
          const isHover  = hoverIdx === i;

          return (
            <div
              key={v.version}
              className="absolute flex flex-col items-center"
              style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
            >
              {/* Circle stop */}
              <div
                className={clsx(
                  'relative z-10 rounded-full flex items-center justify-center border-2 transition-all duration-200 shadow',
                  isActive
                    ? 'w-8 h-8 bg-brand-teal border-brand-teal text-navy-900 shadow-glow scale-110'
                    : isPast
                    ? 'w-6 h-6 bg-brand-teal/70 border-brand-teal/70 text-navy-900'
                    : isHover
                    ? 'w-7 h-7 bg-navy-700 border-brand-teal text-white'
                    : 'w-6 h-6 bg-navy-800 border-navy-600 text-slate-400',
                )}
              >
                <GitBranch size={isActive ? 14 : 11} />
              </div>
              {/* Label below */}
              <div className="absolute top-full pt-2 flex flex-col items-center pointer-events-none">
                <span className={clsx(
                  'text-xs font-mono font-bold transition-colors whitespace-nowrap',
                  isActive ? 'text-brand-teal' : isPast ? 'text-brand-teal/70' : 'text-slate-500',
                )}>
                  {v.version}
                </span>
                <span className="text-[10px] text-slate-600 whitespace-nowrap">
                  {v.concept_count} concepts
                </span>
              </div>
            </div>
          );
        })}

        {/* Scrubber handle (diamond shape) */}
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            left: `${scrubPct}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            transition: 'left 16ms linear',
          }}
        >
          <div className="w-4 h-4 rotate-45 bg-white border-2 border-brand-teal shadow-lg shadow-brand-teal/40" />
        </div>
      </div>
    </div>
  );
}

// ─── Diff summary ─────────────────────────────────────────────────────────────
function DiffSummaryPanel({ diff }: { diff: OntologyDiff | null }) {
  if (!diff) {
    return (
      <div className="p-4 text-slate-600 text-xs">
        <p>Select a version to see changes from the previous cycle.</p>
      </div>
    );
  }
  const { summary } = diff;
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-brand-teal bg-brand-teal/10 border border-brand-teal/20 px-2 py-0.5 rounded">
          {diff.from_version} → {diff.to_version}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StatCard icon={<TrendingUp size={14} className="text-green-400" />} label="Concepts Added"
          value={`+${summary.added_node_count}`} color="text-green-400" />
        <StatCard icon={<TrendingDown size={14} className="text-red-400" />} label="Concepts Removed"
          value={summary.removed_node_count > 0 ? `-${summary.removed_node_count}` : '0'}
          color={summary.removed_node_count > 0 ? 'text-red-400' : 'text-slate-500'} />
        <StatCard icon={<Zap size={14} className="text-blue-400" />} label="New Relations"
          value={summary.new_cognitive_relations != null ? `+${summary.new_cognitive_relations}` : `+${summary.added_edge_count}`}
          color="text-blue-400" />
        <StatCard icon={<Minus size={14} className="text-amber-400" />} label="Survival Rate"
          value={summary.survival_rate != null ? `${(summary.survival_rate * 100).toFixed(0)}%` : '—'}
          color="text-amber-400" />
      </div>

      {(summary.concepts_generated != null || summary.compass_directives != null) && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider">Cycle Details</p>
          {summary.concepts_generated != null && <MetaRow label="Concepts Generated" value={String(summary.concepts_generated)} />}
          {summary.concepts_pruned     != null && <MetaRow label="Concepts Pruned"    value={String(summary.concepts_pruned)} />}
          {summary.compass_directives  != null && <MetaRow label="Compass Directives" value={String(summary.compass_directives)} />}
          {summary.traces_generated    != null && <MetaRow label="Traces Generated"   value={String(summary.traces_generated)} />}
        </div>
      )}

      {diff.added_nodes.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider mb-1.5">
            New Concepts ({diff.added_nodes.length})
          </p>
          <div className="max-h-36 overflow-y-auto space-y-1">
            {diff.added_nodes.map((n) => (
              <div key={n.id} className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-green-400" />
                <span className="text-xs text-slate-300 truncate">{n.name}</span>
                <span className="text-[9px] text-slate-600 flex-shrink-0">{n.entity_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {diff.removed_nodes.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider mb-1.5">
            Removed Concepts ({diff.removed_nodes.length})
          </p>
          <div className="max-h-24 overflow-y-auto space-y-1">
            {diff.removed_nodes.map((n) => (
              <div key={n.id} className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-red-400" />
                <span className="text-xs text-slate-400 truncate line-through">{n.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <div className="bg-navy-800 border border-navy-700 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1">{icon}<span className="text-[10px] text-slate-500">{label}</span></div>
      <p className={clsx('text-lg font-bold font-mono', color)}>{value}</p>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-slate-600">{label}</span>
      <span className="text-xs font-mono text-slate-300">{value}</span>
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────
export function EvolutionTab() {
  const versions         = useOntologyStore((s) => s.versions);
  const selectedVersion  = useOntologyStore((s) => s.selectedVersion);
  const graphByVersion   = useOntologyStore((s) => s.graphByVersion);
  const isPlaying        = useOntologyStore((s) => s.isPlaying);
  const playVersionIndex = useOntologyStore((s) => s.playVersionIndex);
  const graphLoading     = useOntologyStore((s) => s.graphLoading);
  const focusedNodeId    = useOntologyStore((s) => s.focusedNodeId);

  const setVersion          = useOntologyStore((s) => s.setVersion);
  const setFocusedNode      = useOntologyStore((s) => s.setFocusedNode);
  const setIsPlaying        = useOntologyStore((s) => s.setIsPlaying);
  const setPlayVersionIndex = useOntologyStore((s) => s.setPlayVersionIndex);
  const fetchGraph          = useOntologyStore((s) => s.fetchGraph);
  const fetchDiff           = useOntologyStore((s) => s.fetchDiff);

  // EvolutionGraph imperative handle
  const graphRef = useRef<EvolutionGraphHandle | null>(null);

  // Smooth scrubber value: 0 … versions.length-1, can be fractional during transitions
  const [scrubberValue, setScrubberValue] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const initializedRef   = useRef<string | null>(null);
  const transitioningRef = useRef(false);
  const playTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activeDiff, setActiveDiff] = useState<OntologyDiff | null>(null);
  const currentVersionIdx = versions.findIndex((v) => v.version === selectedVersion);

  // ── Core transition logic ─────────────────────────────────────────────────
  const selectVersionByIdx = useCallback(
    async (idx: number) => {
      if (idx < 0 || idx >= versions.length) return;
      if (transitioningRef.current) return;

      transitioningRef.current = true;
      const ver = versions[idx];
      setVersion(ver.version);
      setPlayVersionIndex(idx);

      await fetchGraph(ver.version);

      if (idx > 0) {
        const prev = versions[idx - 1];
        await fetchGraph(prev.version);
        const diff = await fetchDiff(prev.version, ver.version);
        setActiveDiff(diff);

        const newGraph = graphByVersion[ver.version];
        if (newGraph && graphRef.current) {
          const addedNodeIds   = new Set(diff?.added_nodes.map((n) => n.id) ?? []);
          const removedNodeIds = new Set(diff?.removed_nodes.map((n) => n.id) ?? []);

          if (initializedRef.current === null) {
            const prevGraph = graphByVersion[prev.version];
            if (prevGraph) {
              graphRef.current.initGraph(prevGraph);
              initializedRef.current = prev.version;
              await new Promise((r) => setTimeout(r, 600));
            }
          }

          if (diff) {
            setIsTransitioning(true);
            // Base scrubber starts at previous index
            const baseIdx = idx - 1;
            setScrubberValue(baseIdx);

            await graphRef.current.transitionTo(
              newGraph,
              diff,
              addedNodeIds,
              removedNodeIds,
              // onProgress: animate scrubber from baseIdx → idx
              (p) => setScrubberValue(baseIdx + p),
            );

            initializedRef.current = ver.version;
            setIsTransitioning(false);
            setScrubberValue(idx);
          }
        }
      } else {
        setActiveDiff(null);
        const graph = graphByVersion[ver.version];
        if (graph && graphRef.current && initializedRef.current !== ver.version) {
          graphRef.current.initGraph(graph);
          initializedRef.current = ver.version;
        }
        setScrubberValue(0);
      }

      transitioningRef.current = false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [versions, setVersion, setPlayVersionIndex, fetchGraph, fetchDiff, graphByVersion],
  );

  // ── Auto-play ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying) {
      if (playTimerRef.current) clearTimeout(playTimerRef.current);
      return;
    }
    playTimerRef.current = setTimeout(() => {
      const nextIdx = (playVersionIndex + 1) % versions.length;
      selectVersionByIdx(nextIdx).then(() => {
        if (nextIdx === versions.length - 1) setIsPlaying(false);
      });
    }, PLAY_DWELL_MS);
    return () => { if (playTimerRef.current) clearTimeout(playTimerRef.current); };
  }, [isPlaying, playVersionIndex, versions, selectVersionByIdx, setIsPlaying]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (versions.length > 0 && !selectedVersion) {
      selectVersionByIdx(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.length]);

  // ── Init graph when data arrives ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedVersion) return;
    const graph = graphByVersion[selectedVersion];
    if (!graph || !graphRef.current) return;
    if (initializedRef.current === selectedVersion) return;
    graphRef.current.initGraph(graph);
    initializedRef.current = selectedVersion;
  }, [selectedVersion, graphByVersion]);

  const addedNodeIds   = new Set(activeDiff?.added_nodes.map((n) => n.id) ?? []);
  const removedNodeIds = new Set(activeDiff?.removed_nodes.map((n) => n.id) ?? []);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* ── Timeline header ───────────────────────────────────────────────── */}
      <div className="bg-navy-900 border-b border-navy-700 px-8 pt-5 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-white">Knowledge Garden Evolution</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              How the Purchase Decision Ontology grows and refines across research cycles
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => selectVersionByIdx(Math.max(0, currentVersionIdx - 1))}
              disabled={currentVersionIdx <= 0 || isTransitioning}
              className="p-1.5 rounded-lg hover:bg-navy-700 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => {
                if (isPlaying) {
                  setIsPlaying(false);
                } else {
                  if (currentVersionIdx >= versions.length - 1) {
                    initializedRef.current = null;
                    setScrubberValue(0);
                    selectVersionByIdx(0).then(() => setIsPlaying(true));
                  } else {
                    setIsPlaying(true);
                  }
                }
              }}
              disabled={isTransitioning && !isPlaying}
              className="px-3 py-1.5 rounded-lg bg-brand-teal text-navy-900 font-medium text-xs flex items-center gap-1.5 hover:bg-brand-teal/90 transition-colors disabled:opacity-50"
            >
              {isPlaying ? <Pause size={13} /> : <Play size={13} />}
              {isPlaying ? 'Pause' : 'Play Evolution'}
            </button>
            <button
              onClick={() => selectVersionByIdx(Math.min(versions.length - 1, currentVersionIdx + 1))}
              disabled={currentVersionIdx >= versions.length - 1 || isTransitioning}
              className="p-1.5 rounded-lg hover:bg-navy-700 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Scrubber timeline */}
        <VersionScrubber
          versions={versions}
          scrubberValue={scrubberValue}
          onSeek={selectVersionByIdx}
          disabled={isTransitioning}
        />
      </div>

      {/* ── Main area: graph + diff panel ─────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Graph canvas */}
        <div className="flex-1 relative min-h-0 bg-slate-900">
          {graphLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-slate-900/70">
              <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          <EvolutionGraph ref={graphRef} onNodeClick={setFocusedNode} />

          {/* Legend */}
          {activeDiff && (
            <div className="absolute bottom-3 left-3 z-10 flex items-center gap-3 bg-navy-900/90 backdrop-blur border border-navy-700 rounded-lg px-3 py-1.5 text-[10px] font-mono">
              <span className="flex items-center gap-1.5 text-green-400">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> Added ({addedNodeIds.size})
              </span>
              {removedNodeIds.size > 0 && (
                <span className="flex items-center gap-1.5 text-red-400">
                  <span className="w-2 h-2 rounded-full bg-red-400" /> Removed ({removedNodeIds.size})
                </span>
              )}
              <span className="flex items-center gap-1.5 text-slate-400">
                <span className="w-2 h-2 rounded-full bg-slate-400" /> Unchanged
              </span>
            </div>
          )}

          {/* In-progress indicator bar at bottom of graph */}
          {isTransitioning && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-navy-800 z-10">
              <div
                className="h-full bg-brand-teal transition-none"
                style={{
                  width: `${((scrubberValue - Math.floor(scrubberValue)) * 100).toFixed(1)}%`,
                  transition: 'width 16ms linear',
                }}
              />
            </div>
          )}
        </div>

        {/* Diff summary panel */}
        <div className="w-72 flex-shrink-0 bg-navy-900 border-l border-navy-700 overflow-y-auto">
          <div className="p-3 border-b border-navy-800">
            <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider">
              Cycle Changes
            </p>
          </div>
          <DiffSummaryPanel diff={activeDiff} />
        </div>
      </div>

      {/* Node inspector overlay */}
      {focusedNodeId && (
        <div className="absolute right-72 top-0 bottom-0 z-20 shadow-xl">
          <NodeInspectorPanel
            nodeId={focusedNodeId}
            onClose={() => setFocusedNode(null)}
          />
        </div>
      )}
    </div>
  );
}
